import useTranslation from "@hooks/use_translation";
import useDevice from "@/hooks/use_device";
import { Trans } from "react-i18next";
import { Body, PageHeader } from "@sprout/typography";
import DreamJobIcon from "@/sprout/icons/dream_job";
import DreamJobPickerModal from "@components/applications/dream_job_picker_modal";
import Fingerprint1Orange from "../../images/fingerprint_1_orange.svg";
import Fingerprint3 from "../../images/fingerprint_3.svg";

export type DreamJobBannerState = "unused_no_apps" | "unused_with_apps" | "used";

type TagVariant = "available" | "unavailable";

type DreamJobTagProps = {
  label: string;
  variant: TagVariant;
};

const DreamJobTag = ({ label, variant }: DreamJobTagProps) => {
  const unavailable = variant === "unavailable";
  const { isMobile } = useDevice();
  return (
    <div
      className={`flex items-center gap-2 rounded-[5px] px-3 py-1 ${
        unavailable ? "bg-green-700" : "bg-marigold-e100"
      }`}
    >
      <DreamJobIcon size={isMobile ? "xs" : "sm"} color={unavailable ? "white" : "dark-green"} />
      <Body className={`text-xs! md:text-base! ${unavailable ? "text-white!" : "text-dark-green"}`}>
        {label}
      </Body>
    </div>
  );
};

const STATE_CONFIG: Record<DreamJobBannerState, { image: string; tagVariant: TagVariant }> = {
  unused_no_apps: { image: Fingerprint1Orange, tagVariant: "available" },
  unused_with_apps: { image: Fingerprint1Orange, tagVariant: "available" },
  used: { image: Fingerprint3, tagVariant: "unavailable" },
} as const;

type Props = {
  state: DreamJobBannerState;
  count: number;
};

const DreamJobBanner = ({ state, count }: Props) => {
  const { image, tagVariant } = STATE_CONFIG[state];
  const unavailable = tagVariant === "unavailable";
  const { t } = useTranslation("dashboard", {
    keyPrefix: `header.dream_job_banner.${state}`,
  });

  return (
    <div
      className={`relative overflow-hidden w-full rounded-lg ${
        unavailable ? "bg-green-e50" : "bg-marigold-25"
      }`}
    >
      <div className="relative mx-auto max-w-[1550px] pt-[51px] pb-[81px] px-[52px]">
        <img
          src={image}
          alt=""
          aria-hidden
          className="pointer-events-none select-none absolute -top-[187px] w-[600px] h-[600px] -right-[150px] opacity-40 md:right-0 md:opacity-100"
        />
        <div className="relative flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex flex-col items-start gap-3 max-w-3xl">
            <DreamJobTag label={t("countdown", { count })} variant={tagVariant} />
            <PageHeader className="text-[32px]! md:text-[38px]!">{t("title")}</PageHeader>
            <Body>
              <Trans
                i18nKey={`header.dream_job_banner.${state}.subtitle`}
                ns="dashboard"
                components={{ bold: <Body medium as="span" /> }}
              />
            </Body>
          </div>
          {state === "unused_with_apps" && (
            <div className="flex w-full flex-col md:w-auto">
              <DreamJobPickerModal label={t("cta")} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DreamJobBanner;
